import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Eye, Copy, ChartPolar, Trophy, Users, UploadSimple, FileText, EnvelopeSimple, EnvelopeOpen, CheckCircle, ArrowClockwise, Trash, ArrowCounterClockwise, Archive, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { ScoreRadar, CompareRadar, ScoreBars, IndicesBadges, CandidateRanking } from "@/components/ScoreCharts";
import PageHeader from "@/components/PageHeader";
import ResizableDialogContent from "@/components/ResizableDialog";
import CandidateDetailDialog from "@/components/CandidateDetailDialog";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAND_ARCHIVE_REASONS = [
  { value: "non_assunto", label: "Non assunto" },
  { value: "ritirato", label: "Ritirato" },
  { value: "non_interessato", label: "Non interessato" },
  { value: "altro", label: "Altro" },
];

export default function CandidatesPage() {
  const { activeWorkspaceId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewCandId, setViewCandId] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [rankingPosition, setRankingPosition] = useState(null);
  const [rankedCandidates, setRankedCandidates] = useState([]);
  const [filter, setFilter] = useState("");
  // MIG-22: tab state (active|trash|archive)
  const [tab, setTab] = useState(() => searchParams.get("tab") || "active");
  // MIG-21: position filter (URL-persisted)
  const [posFilter, setPosFilter] = useState(() => searchParams.get("position") || "all");
  // MIG-24: sort
  const [sortDir, setSortDir] = useState(() => searchParams.get("sort") || "desc");
  const [trashedCands, setTrashedCands] = useState([]);
  const [archivedCands, setArchivedCands] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  // MIG-22: archive dialog
  const [archiveDlg, setArchiveDlg] = useState(null); // candidate object
  const [archiveReason, setArchiveReason] = useState("non_assunto");
  const [archiveNotes, setArchiveNotes] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [form, setForm] = useState({ first_name:"",last_name:"",email:"",phone:"",birth_date:"",city:"",zip_code:"",position_id:"",hr_notes:"" });

  const load = () => {
    axios.get(`${API}/candidates`, { withCredentials: true }).then(r => setCandidates(r.data)).catch(() => {});
    axios.get(`${API}/candidates?status=cestino`, { withCredentials: true }).then(r => setTrashedCands(r.data)).catch(() => {});
    axios.get(`${API}/candidates?status=archivio`, { withCredentials: true }).then(r => setArchivedCands(r.data)).catch(() => {});
    axios.get(`${API}/positions`, { withCredentials: true }).then(r => setPositions(r.data)).catch(() => {});
  };
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { load(); }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // B71 FIX-3: refetch on focus (utente torna sulla tab, mostra sempre dati aggiornati)
  useRefetchOnFocus(load);

  // MIG-21/22/24: persist tab + filter + sort in URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (posFilter && posFilter !== "all") next.set("position", posFilter); else next.delete("position");
    if (sortDir !== "desc") next.set("sort", sortDir); else next.delete("sort");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, posFilter, sortDir]);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await axios.post(`${API}/candidates`, form, { withCredentials: true });
      toast.success("Candidato aggiunto");
      setOpen(false); setForm({ first_name:"",last_name:"",email:"",phone:"",birth_date:"",city:"",zip_code:"",position_id:"" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const copyTestLink = (link) => {
    navigator.clipboard.writeText(`${window.location.origin}/analisi/candidato/${link}`);
    toast.success("Link analisi copiato");
  };

  const toggleCompareSelect = (c) => {
    setSelectedForCompare(prev => {
      const exists = prev.find(x => x.id === c.id);
      if (exists) return prev.filter(x => x.id !== c.id);
      if (prev.length >= 5) { toast.info("Massimo 5 candidati per confronto"); return prev; }
      return [...prev, c];
    });
  };

  const loadRanking = async (posId) => {
    setRankingPosition(posId);
    try {
      const { data } = await axios.get(`${API}/candidates/compare/${posId}`, { withCredentials: true });
      setRankedCandidates(data);
    } catch { setRankedCandidates([]); }
  };

  const deleteCandidate = async (cand) => {
    try {
      await axios.delete(`${API}/candidates/${cand.id}`, { withCredentials: true });
      toast.success(`${cand.first_name} ${cand.last_name} spostato nel cestino`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const restoreCandidate = async (cid) => {
    try {
      await axios.post(`${API}/candidates/${cid}/restore`, {}, { withCredentials: true });
      toast.success("Candidato ripristinato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ripristino"); }
  };

  // B71 BIS — Hard-delete (eliminazione definitiva) da tab Cestino
  const hardDeleteCandidate = async (cand) => {
    try {
      await axios.delete(`${API}/candidates/${cand.id}?permanent=true`, { withCredentials: true });
      toast.success(`${cand.first_name} ${cand.last_name} eliminato definitivamente`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione definitiva"); }
  };

  // MIG-22: archive flow with reason
  const openArchiveDialog = (cand) => {
    setArchiveDlg(cand);
    setArchiveReason("non_assunto");
    setArchiveNotes("");
  };
  const submitArchive = async () => {
    if (!archiveDlg) return;
    setArchiveBusy(true);
    try {
      await axios.post(
        `${API}/candidates/${archiveDlg.id}/archive`,
        { reason: archiveReason, notes: archiveNotes },
        { withCredentials: true },
      );
      toast.success(`${archiveDlg.first_name} ${archiveDlg.last_name} archiviato`);
      setArchiveDlg(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore archiviazione"); }
    finally { setArchiveBusy(false); }
  };
  const unarchiveCandidate = async (cid) => {
    try {
      await axios.post(`${API}/candidates/${cid}/unarchive`, {}, { withCredentials: true });
      toast.success("Candidato riattivato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // MIG-23: position lookup
  const positionTitle = (pid) => positions.find(p => p.id === pid)?.title || "—";

  const statusColors = { "In attesa": "bg-yellow-50 text-yellow-700", "In corso": "bg-blue-50 text-blue-700", "Completato": "bg-green-50 text-green-700", "Non idoneo": "bg-red-50 text-red-600" };
  const filtered = (() => {
    let list = candidates; // active list (no archived/trashed — backend filters)
    if (posFilter && posFilter !== "all") list = list.filter(c => c.position_id === posFilter);
    if (statusFilter && statusFilter !== "all") list = list.filter(c => c.status === statusFilter);
    if (sortBy === "score_desc") list = [...list].sort((a, b) => (b.match_score_num || 0) - (a.match_score_num || 0));
    else if (sortBy === "score_asc") list = [...list].sort((a, b) => (a.match_score_num || 0) - (b.match_score_num || 0));
    else {
      // MIG-24: sort by created_at (data candidatura)
      list = [...list].sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const dbb = new Date(b.created_at || 0).getTime();
        return sortDir === "asc" ? da - dbb : dbb - da;
      });
    }
    return list;
  })();
  const compareProfiles = selectedForCompare.filter(c => c.scores).map(c => ({ name: `${c.first_name} ${c.last_name}`, scores: c.scores }));

  return (
    <div className="animate-fade-in" data-testid="candidates-page">
      <PageHeader title="Candidati" subtitle={`${candidates.length} candidati totali`}>
          <Select value={posFilter} onValueChange={setPosFilter}>
            <SelectTrigger className="w-[200px] h-10 border-[#0D1F3C]/15" data-testid="position-filter-trigger"><SelectValue placeholder="Tutte le posizioni" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le posizioni</SelectItem>
              {positions.filter(p => p.status === "Aperta").map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-10"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="In attesa">In attesa</SelectItem><SelectItem value="In corso">In corso</SelectItem><SelectItem value="Completato">Completato</SelectItem><SelectItem value="Non idoneo">Non idoneo</SelectItem></SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px] h-10"><SelectValue placeholder="Ordina per..." /></SelectTrigger>
            <SelectContent><SelectItem value="all">Predefinito</SelectItem><SelectItem value="score_desc">Score (alto→basso)</SelectItem><SelectItem value="score_asc">Score (basso→alto)</SelectItem></SelectContent>
          </Select>
          {posFilter && posFilter !== "all" && (
            <Button variant="outline" onClick={() => loadRanking(posFilter)} data-testid="ranking-btn"
              className="border-[#56A3F5]/30 text-[#1B4FD8] hover:bg-[#56A3F5]/10">
              <Trophy size={18} className="mr-2" />Classifica
            </Button>
          )}
          <Button variant={compareMode ? "default" : "outline"} onClick={() => { setCompareMode(!compareMode); setSelectedForCompare([]); }}
            data-testid="compare-mode-btn" className={compareMode ? "bg-[#56A3F5] hover:bg-[#1B4FD8]" : "border-[#0D1F3C]/15"}>
            <ChartPolar size={18} className="mr-2" />{compareMode ? "Esci Confronto" : "Confronta"}
          </Button>
          {compareMode && selectedForCompare.length >= 2 && (
            <Button onClick={() => setCompareOpen(true)} data-testid="open-compare-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]">
              Confronta ({selectedForCompare.length})
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-candidate-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]"><Plus size={18} className="mr-2" />Aggiungi</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Aggiungi Candidato</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Nome *</Label><Input data-testid="cand-first-name" value={form.first_name} onChange={e=>up("first_name",e.target.value)} required /></div>
                  <div><Label className="text-xs mb-1 block">Cognome *</Label><Input data-testid="cand-last-name" value={form.last_name} onChange={e=>up("last_name",e.target.value)} required /></div>
                </div>
                <div><Label className="text-xs mb-1 block">Email *</Label><Input data-testid="cand-email" type="email" value={form.email} onChange={e=>up("email",e.target.value)} required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs mb-1 block">Telefono</Label><Input value={form.phone} onChange={e=>up("phone",e.target.value)} /></div>
                  <div><Label className="text-xs mb-1 block">Data di nascita</Label><Input type="date" value={form.birth_date} onChange={e=>up("birth_date",e.target.value)} /></div>
                </div>
                <div><Label className="text-xs mb-1 block">Posizione</Label>
                  <Select value={form.position_id} onValueChange={v=>up("position_id",v)}>
                    <SelectTrigger data-testid="cand-position"><SelectValue placeholder="Seleziona posizione" /></SelectTrigger>
                    <SelectContent>{positions.filter(p=>p.status==="Aperta").map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Note HR <span className="text-[#0D1F3C]/40">(opzionale)</span></Label>
                  <Textarea rows={3} value={form.hr_notes} onChange={e=>up("hr_notes", e.target.value)}
                    placeholder="Es: richiamare martedi · preferenze orario · osservazioni colloquio telefonico" data-testid="cand-hr-notes" />
                  <p className="text-[10px] text-[#0D1F3C]/50 mt-1">Le note operative non influenzano la valutazione comportamentale. Solo info rilevanti (aspettative, motivazioni) saranno considerate nel report.</p>
                </div>
                <Button onClick={save} data-testid="cand-save-btn" className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]">Aggiungi</Button>
              </div>
            </DialogContent>
          </Dialog>
      </PageHeader>

      <CandidateDetailDialog candId={viewCandId} open={!!viewCandId} onClose={() => setViewCandId(null)} onRefresh={load} positions={positions} />

      {/* Compare Radar Dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <ResizableDialogContent defaultW={850} defaultH={650}>
          <DialogHeader><DialogTitle>Confronto Candidati</DialogTitle></DialogHeader>
          {compareProfiles.length >= 2 ? (
            <div className="mt-4">
              <CompareRadar profiles={compareProfiles} size={420} />
              <div className="mt-4 grid grid-cols-2 gap-3">
                {compareProfiles.map((p, i) => {
                  const bandLabel = p.avg_view?.label || "N/A";
                  const bandColor = p.avg_view?.color || "#999";
                  return (
                    <div key={i} className="p-3 rounded-lg border border-[#0D1F3C]/8 flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ["#1B4FD8","#56A3F5","#10B981","#F59E0B","#EF4444"][i] }} />
                      <div className="flex-1"><p className="text-sm font-medium text-[#0D1F3C]">{p.name}</p></div>
                      <p className="text-sm font-bold" style={{ color: bandColor }}>{bandLabel}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <p className="text-sm text-[#0D1F3C]/40 py-8 text-center">Seleziona almeno 2 candidati con analisi completata</p>}
        </ResizableDialogContent>
      </Dialog>

      {/* Ranking Dialog */}
      <Dialog open={!!rankingPosition} onOpenChange={() => setRankingPosition(null)}>
        <ResizableDialogContent defaultW={780} defaultH={650}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy size={22} weight="duotone" className="text-[#F59E0B]" />
              Classifica Candidati
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {rankedCandidates.length > 0 ? (
              <>
                {rankedCandidates.length >= 2 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-[#0D1F3C] mb-3">Confronto Top Candidati</h4>
                    <CompareRadar profiles={rankedCandidates.slice(0, 3).map(c => ({
                      name: `${c.first_name} ${c.last_name}`,
                      scores: c.scores
                    }))} size={340} />
                  </div>
                )}
                <h4 className="text-sm font-medium text-[#0D1F3C] mb-3">Classifica per Media Punteggi</h4>
                <CandidateRanking candidates={rankedCandidates} onSelect={(c) => { setRankingPosition(null); setViewCandId(c.id); }} />
              </>
            ) : <p className="text-sm text-[#0D1F3C]/40 text-center py-8">Nessun candidato con analisi completata per questa posizione</p>}
          </div>
        </ResizableDialogContent>
      </Dialog>

      {/* MIG-22: Tabs Attivi | Archivio | Cestino */}
      <Tabs value={tab} onValueChange={setTab} className="mb-3">
        <TabsList data-testid="cand-tabs-list">
          <TabsTrigger value="active" data-testid="cand-tab-active">
            Attivi <Badge className="ml-2 bg-[#1B4FD8]/10 text-[#1B4FD8]">{candidates.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="cand-tab-archive">
            <Archive size={14} className="mr-1.5" />
            Archivio <Badge className="ml-2 bg-[#0D1F3C]/10 text-[#0D1F3C]">{archivedCands.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="trash" data-testid="cand-tab-trash">
            <Trash size={14} className="mr-1.5" />
            Cestino <Badge className="ml-2 bg-red-50 text-red-600">{trashedCands.length}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Table */}
      {tab === "active" && (
      <Card className="border-[#0D1F3C]/8">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {compareMode && <TableHead className="w-10" />}
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Nome</TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Email</TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Posizione</TableHead>
              <TableHead
                className="text-xs uppercase text-[#0D1F3C]/50 cursor-pointer select-none hover:text-[#1B4FD8]"
                onClick={() => setSortDir(sortDir === "desc" ? "asc" : "desc")}
                data-testid="sort-by-date-th"
                title="Ordina per data candidatura"
              >
                Data candidatura {sortDir === "desc" ? <ArrowDown size={11} className="inline ml-1" /> : <ArrowUp size={11} className="inline ml-1" />}
              </TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Status</TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Score</TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Match</TableHead>
              <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={compareMode ? 7 : 6} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Nessun candidato</TableCell></TableRow> :
              filtered.map(c => {
                const pos = positions.find(p => p.id === c.position_id);
                const bandLabel = c.avg_view?.label || (c.scores ? "N/A" : null);
                const bandColor = c.avg_view?.color || "#999";
                const isSelected = selectedForCompare.find(x => x.id === c.id);
                return (
                  <TableRow key={c.id} className={`hover:bg-[#F4F7FB]/50 ${isSelected ? 'bg-[#1B4FD8]/5' : ''}`} data-testid={`candidate-row-${c.id}`}>
                    {compareMode && (
                      <TableCell>
                        <Checkbox checked={!!isSelected} onCheckedChange={() => toggleCompareSelect(c)} disabled={!c.scores}
                          data-testid={`compare-check-${c.id}`} />
                      </TableCell>
                    )}
                    <TableCell><button onClick={() => setViewCandId(c.id)} className="font-medium text-[#0D1F3C] hover:text-[#1B4FD8] transition-colors cursor-pointer">{c.first_name} {c.last_name}</button></TableCell>
                    <TableCell className="text-sm text-[#0D1F3C]/60">{c.email}</TableCell>
                    <TableCell className="text-sm">
                      {pos ? (
                        <button
                          onClick={() => navigate(`/positions?expand=${pos.id}`)}
                          className="text-[#1B4FD8] hover:underline"
                          title="Vai alla posizione"
                          data-testid={`pos-link-${c.id}`}
                        >{pos.title}</button>
                      ) : <span className="text-[#0D1F3C]/30">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[#0D1F3C]/60 font-mono whitespace-nowrap" data-testid={`cand-date-${c.id}`}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}
                    </TableCell>
                    <TableCell><Badge className={`text-xs ${statusColors[c.status] || ''}`}>{c.status}</Badge>
                      {c.report_generation_status === "generating" && (
                        <Badge className="ml-1 text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-testid={`gen-badge-${c.id}`}>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1 animate-pulse" />
                          Report in elaborazione
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {bandLabel ? (
                        <Badge className="text-xs font-medium" style={{ backgroundColor: `${bandColor}20`, color: bandColor, border: `1px solid ${bandColor}40` }}>
                          {bandLabel}
                        </Badge>
                      ) : <span className="text-xs text-[#0D1F3C]/30">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.match_score ? (
                        <Badge className={`text-xs ${c.match_score === 'Alta' ? 'bg-green-50 text-green-700' : c.match_score === 'Parziale' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-600'}`}>{c.match_score}</Badge>
                      ) : <span className="text-xs text-[#0D1F3C]/30">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setViewCandId(c.id)} data-testid={`view-cand-${c.id}`} title="Visualizza scheda"><Eye size={16} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => copyTestLink(c.test_link)} data-testid={`copy-test-${c.id}`} title="Copia link test"><Copy size={16} /></Button>
                        {(() => {
                          if (c.status === "Completato" || c.test_completed_at) {
                            return (
                              <span className="inline-flex items-center justify-center w-8 h-8" title="Analisi completata" data-testid={`test-done-${c.id}`}>
                                <CheckCircle size={16} weight="fill" className="text-green-600" />
                              </span>
                            );
                          }
                          const sentAt = c.test_link_sent_at;
                          const sent = !!sentAt || !!c.test_email_sent;
                          const date = sentAt ? new Date(sentAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
                          const title = sent
                            ? (date ? `Link gia inviato il ${date} — clicca per rinviare` : "Link gia inviato — clicca per rinviare")
                            : (c.email ? "Invia link analisi via email" : "Nessuna email candidato");
                          const handleSend = async () => {
                            try {
                              await axios.post(`${API}/candidates/${c.id}/send-test-email`, {}, { withCredentials: true });
                              toast.success(sent ? "Link rinviato via email" : "Email inviata");
                              load();
                            } catch { toast.error("Errore invio email"); }
                          };
                          if (!c.email) {
                            return (
                              <span className="inline-flex items-center justify-center w-8 h-8 opacity-30" title="Candidato senza email">
                                <EnvelopeSimple size={16} />
                              </span>
                            );
                          }
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`email-test-${c.id}`}
                              onClick={handleSend}
                              title={title}
                              className="relative"
                            >
                              {sent ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <EnvelopeOpen size={16} weight="duotone" className="text-green-600" />
                                  <ArrowClockwise size={10} className="text-[#0D1F3C]/50" />
                                </span>
                              ) : (
                                <EnvelopeSimple size={16} className="text-[#1B4FD8]" />
                              )}
                            </Button>
                          );
                        })()}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openArchiveDialog(c)}
                          title="Archivia (chiusura gestionale)"
                          data-testid={`archive-cand-${c.id}`}
                          className="text-[#0D1F3C]/60 hover:text-[#0D1F3C]"
                        >
                          <Archive size={16} />
                        </Button>
                        <ConfirmDelete
                          title="Sposta nel cestino"
                          description={`Spostare ${c.first_name} ${c.last_name} nel cestino? Potrai ripristinarlo entro 30 giorni.`}
                          onConfirm={() => deleteCandidate(c)}
                        >
                          <Button variant="ghost" size="sm" title="Sposta nel cestino" data-testid={`delete-cand-${c.id}`} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash size={16} />
                          </Button>
                        </ConfirmDelete>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Card>
      )}

      {/* MIG-22: Archivio tab */}
      {tab === "archive" && (
        <Card className="border-[#0D1F3C]/8" data-testid="candidates-archive-card">
          <div className="flex items-center justify-between p-4 border-b border-[#0D1F3C]/8">
            <div>
              <h3 className="text-sm font-semibold text-[#0D1F3C]">Archivio candidati</h3>
              <p className="text-xs text-[#0D1F3C]/50">Candidati gestiti ma chiusi (non assunti, ritirati, non interessati). Restano consultabili senza scadenze.</p>
            </div>
            <Badge className="bg-[#0D1F3C]/10 text-[#0D1F3C]">{archivedCands.length} elementi</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Nome</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Email</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Posizione</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Motivo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Archiviato il</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {archivedCands.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Nessun candidato in archivio</TableCell></TableRow>
              ) : archivedCands.map(c => (
                <TableRow key={c.id} data-testid={`archive-row-${c.id}`}>
                  <TableCell><button onClick={() => setViewCandId(c.id)} className="font-medium text-[#0D1F3C] hover:text-[#1B4FD8]">{c.first_name} {c.last_name}</button></TableCell>
                  <TableCell className="text-sm text-[#0D1F3C]/60">{c.email}</TableCell>
                  <TableCell className="text-sm text-[#0D1F3C]/60">{positionTitle(c.position_id)}</TableCell>
                  <TableCell><Badge className="text-xs bg-[#0D1F3C]/8 text-[#0D1F3C]">{(CAND_ARCHIVE_REASONS.find(r => r.value === c.archive_reason) || {}).label || c.archive_reason || "altro"}</Badge></TableCell>
                  <TableCell className="text-sm text-[#0D1F3C]/60">{c.archived_at ? new Date(c.archived_at).toLocaleString("it-IT") : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => unarchiveCandidate(c.id)} data-testid={`unarchive-cand-${c.id}`} className="text-[#1B4FD8] border-[#1B4FD8]/30">
                      <ArrowCounterClockwise size={14} className="mr-1" />Riattiva
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Cestino tab */}
      {tab === "trash" && (
        <Card className="border-[#0D1F3C]/8" data-testid="candidates-trash-card">
          <div className="flex items-center justify-between p-4 border-b border-[#0D1F3C]/8">
            <div>
              <h3 className="text-sm font-semibold text-[#0D1F3C]">Cestino candidati</h3>
              <p className="text-xs text-[#0D1F3C]/50">I candidati nel cestino vengono eliminati definitivamente dopo 30 giorni</p>
            </div>
            <Badge className="bg-[#0D1F3C]/10 text-[#0D1F3C]">{trashedCands.length} elementi</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Nome</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Email</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Eliminato il</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trashedCands.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Cestino vuoto</TableCell></TableRow>
              ) : trashedCands.map(c => (
                <TableRow key={c.id} data-testid={`trash-row-${c.id}`}>
                  <TableCell className="font-medium text-[#0D1F3C]">{c.first_name} {c.last_name}</TableCell>
                  <TableCell className="text-sm text-[#0D1F3C]/60">{c.email}</TableCell>
                  <TableCell className="text-sm text-[#0D1F3C]/60">{c.deleted_at ? new Date(c.deleted_at).toLocaleString("it-IT") : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => restoreCandidate(c.id)} data-testid={`restore-cand-${c.id}`} className="text-[#1B4FD8] border-[#1B4FD8]/30">
                        <ArrowCounterClockwise size={14} className="mr-1" />Ripristina
                      </Button>
                      <ConfirmDelete
                        title="Eliminare definitivamente?"
                        description={`Stai per eliminare DEFINITIVAMENTE ${c.first_name} ${c.last_name} dal database. Verranno cancellati anche tutti i dati collegati (analisi, report, CV, colloqui). L'email tornera libera per nuove candidature. Questa azione NON puo essere annullata.`}
                        onConfirm={() => hardDeleteCandidate(c)}
                      >
                        <Button variant="ghost" size="sm" title="Elimina definitivamente" data-testid={`hard-delete-cand-${c.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash size={14} className="mr-1" />Elimina definitivamente
                        </Button>
                      </ConfirmDelete>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* MIG-22: Archive Dialog */}
      <Dialog open={!!archiveDlg} onOpenChange={(v) => !v && setArchiveDlg(null)}>
        <DialogContent className="max-w-md" data-testid="archive-cand-dialog">
          <DialogHeader>
            <DialogTitle>Archivia candidato</DialogTitle>
          </DialogHeader>
          {archiveDlg && (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-[#0D1F3C]/70">
                <strong>{archiveDlg.first_name} {archiveDlg.last_name}</strong> · {positionTitle(archiveDlg.position_id)}
              </p>
              <div>
                <Label className="text-xs mb-1 block">Motivo archiviazione *</Label>
                <Select value={archiveReason} onValueChange={setArchiveReason}>
                  <SelectTrigger data-testid="archive-reason-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAND_ARCHIVE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Note <span className="text-[#0D1F3C]/40">(opzionale)</span></Label>
                <Textarea rows={2} value={archiveNotes} onChange={e => setArchiveNotes(e.target.value)} placeholder="Es. ha rifiutato l'offerta · candidato in altra posizione · feedback cliente" data-testid="archive-notes" />
              </div>
              <p className="text-[11px] text-[#0D1F3C]/50">L'archivio non ha scadenza. Il candidato resta consultabile e può essere riattivato in ogni momento.</p>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setArchiveDlg(null)}>Annulla</Button>
            <Button onClick={submitArchive} disabled={archiveBusy} className="bg-[#0D1F3C] hover:bg-[#0D1F3C]/80" data-testid="archive-confirm-btn">
              {archiveBusy ? "Archiviazione…" : "Archivia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
